/**
 * HomeVideoAdmin — set/update the presentation video URL shown at the top-right
 * of the H0 Portal (public homepage). Supports Loom share URLs (auto-converted
 * to embed) plus generic YouTube / direct video URLs.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const HomeVideoAdmin = () => {
  const [url, setUrl] = useState('');
  const [embed, setEmbed] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/home-video`);
      setUrl(r.data?.url || '');
      setEmbed(r.data?.embed_url || '');
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.put(
        `${API_URL}/api/zona-pro/home-video`,
        { url },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEmbed(r.data?.embed_url || '');
      toast.success('Vídeo actualitzat');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Vols amagar el botó Video de la home?')) return;
    setUrl('');
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/zona-pro/home-video`,
        { url: '' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEmbed('');
      toast.success('Botó Video amagat');
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="home-video-admin">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-lg">🎥 Vídeo de presentació</h3>
            <p className="text-sm text-slate-600 mt-1">
              L&apos;URL que enganxis aquí es mostrarà com a botó <strong>Video</strong> a la home,
              a l&apos;esquerra del selector d&apos;idiomes. Suporta Loom, YouTube, Vimeo o vídeo directe.
              Si el deixes buit, el botó desapareix.
            </p>
          </div>

          <div>
            <Label className="text-xs">URL del vídeo</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.loom.com/share/…"
              data-testid="home-video-url-input"
            />
            {loading ? (
              <div className="text-xs text-slate-400 mt-1">Carregant…</div>
            ) : embed && (
              <div className="mt-2 text-xs bg-emerald-50 text-emerald-700 rounded px-2 py-1 break-all">
                ✓ Es mostrarà com: <span className="font-mono">{embed}</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="home-video-save"
            >
              {saving ? 'Guardant…' : 'Guardar'}
            </Button>
            {url && (
              <Button
                variant="outline"
                onClick={clear}
                disabled={saving}
                className="text-red-600"
                data-testid="home-video-clear"
              >
                Amagar botó
              </Button>
            )}
          </div>

          {embed && (
            <div className="mt-4">
              <Label className="text-xs">Previsualització</Label>
              <div className="mt-1 aspect-video bg-black rounded-lg overflow-hidden">
                <iframe
                  title="preview"
                  src={embed}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HomeVideoAdmin;
